import { useState } from 'react';
import { api, fmt, type Category, type Product } from '../api';
import { useAction, useAsync } from '../hooks';

/** Лавка: категории с минимальными ценами, товары, загрузка кодов (ТЗ §7.1). */
export function ShopPage() {
  const categories = useAsync(() => api.categories(), []);
  const products = useAsync(() => api.products(), []);

  return (
    <>
      <h1>Лавка</h1>
      <p className="subtitle">Товары, цены в бонусах, минимальные цены по категориям и склад кодов.</p>

      <h2>Категории</h2>
      <p className="subtitle" style={{ marginTop: -4 }}>
        Минимальная цена задаётся здесь, а не в коде. «Строго больше» нужно для товаров, выносящих ценность наружу:
        за одного приглашённого нельзя сразу получить сертификат.
      </p>
      <div className="card">
        {categories.error && <div className="notice err">{categories.error}</div>}
        <table>
          <thead>
            <tr>
              <th>Категория</th>
              <th>Выносит ценность наружу</th>
              <th className="num">Минимум, бонусов</th>
              <th>Сравнение</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(categories.data?.items ?? []).map((c) => (
              <CategoryRow key={c.id} category={c} onSaved={() => { categories.reload(); products.reload(); }} />
            ))}
          </tbody>
        </table>
      </div>

      <h2>Товары</h2>
      {products.error && <div className="notice err">{products.error}</div>}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Товар</th>
              <th>Категория</th>
              <th className="num">Цена</th>
              <th>Выдача</th>
              <th className="num">Склад</th>
              <th>Активен</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(products.data?.items ?? []).map((p) => (
              <ProductRow key={p.id} product={p} onChanged={products.reload} />
            ))}
          </tbody>
        </table>
      </div>

      <NewProduct categories={categories.data?.items ?? []} onCreated={products.reload} />
    </>
  );
}

function CategoryRow({ category, onSaved }: { category: Category; onSaved: () => void }) {
  const [min, setMin] = useState(String(category.min_price_bonuses));
  const [strict, setStrict] = useState(category.min_price_strict);
  const [violations, setViolations] = useState<{ id: string; title: string; price_bonuses: number }[]>([]);

  const save = useAction(async () => {
    const r = await api.saveCategory(category.id, {
      min_price_bonuses: Number(min),
      min_price_strict: strict,
    } as Partial<Category>);
    setViolations(r.violatingProducts);
    onSaved();
  });

  const dirty = Number(min) !== category.min_price_bonuses || strict !== category.min_price_strict;

  return (
    <>
      <tr>
        <td>{category.title}</td>
        <td>{category.value_outflow ? <span className="badge warn">да</span> : <span className="badge off">нет</span>}</td>
        <td className="num">
          <input type="number" min={0} value={min} onChange={(e) => setMin(e.target.value)} />
        </td>
        <td>
          <select value={String(strict)} onChange={(e) => setStrict(e.target.value === 'true')}>
            <option value="true">строго больше</option>
            <option value="false">можно от</option>
          </select>
        </td>
        <td>
          <button className="secondary small" disabled={!dirty || save.busy} onClick={() => void save.run()}>
            Сохранить
          </button>
        </td>
      </tr>
      {violations.length > 0 && (
        <tr>
          <td colSpan={5}>
            <div className="notice warn" style={{ margin: 0 }}>
              После изменения минимума эти активные товары продаются ниже порога и будут отклоняться при покупке:{' '}
              {violations.map((v) => `${v.title} (${fmt(v.price_bonuses)})`).join(', ')}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ProductRow({ product, onChanged }: { product: Product; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState('');
  const [price, setPrice] = useState(String(product.price_bonuses));

  const toggle = useAction(async () => {
    await api.updateProduct(product.id, { isActive: !product.is_active });
    onChanged();
  });
  const savePrice = useAction(async () => {
    await api.updateProduct(product.id, { priceBonuses: Number(price) });
    onChanged();
  });
  const upload = useAction(async () => {
    const list = codes.split(/[\r\n,;]+/).map((c) => c.trim()).filter(Boolean);
    await api.uploadCodes(product.id, list);
    setCodes('');
    onChanged();
  });

  return (
    <>
      <tr>
        <td>
          {product.title}
          <div className="badge off">{product.sku}</div>
        </td>
        <td>{product.category_title}</td>
        <td className="num">{fmt(product.price_bonuses)}</td>
        <td>
          {product.delivery_kind === 'CODE_POOL'
            ? 'Код из пула'
            : product.delivery_kind === 'PROMO_ON_DEMAND'
              ? `Промокод ${fmt((product.promo_nominal_kopecks ?? 0) / 100)} ₽`
              : 'Без выдачи'}
        </td>
        <td className="num">
          {product.delivery_kind === 'CODE_POOL' ? (
            <span className={product.stock_available === 0 ? 'badge err' : 'badge'}>
              {product.stock_available} / выдано {product.stock_issued}
            </span>
          ) : (
            '—'
          )}
        </td>
        <td>
          <button className={product.is_active ? 'secondary small' : 'small'} disabled={toggle.busy} onClick={() => void toggle.run()}>
            {product.is_active ? 'Скрыть' : 'Показать'}
          </button>
        </td>
        <td>
          <button className="secondary small" onClick={() => setOpen((v) => !v)}>
            {open ? 'Свернуть' : 'Изменить'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7}>
            <div className="grid cols-2">
              <div>
                <div className="field">
                  <label>Цена в бонусах</label>
                  <div className="row">
                    <input type="number" min={1} value={price} onChange={(e) => setPrice(e.target.value)} />
                    <button className="secondary small" disabled={savePrice.busy} onClick={() => void savePrice.run()}>
                      Сохранить
                    </button>
                  </div>
                </div>
                {savePrice.error && <div className="notice err">{savePrice.error}</div>}
              </div>

              {product.delivery_kind === 'CODE_POOL' && (
                <div>
                  <div className="field">
                    <label>Загрузить коды — по одному в строке (ТЗ §5.5)</label>
                    <textarea rows={5} value={codes} onChange={(e) => setCodes(e.target.value)} />
                  </div>
                  <button className="secondary small" disabled={!codes.trim() || upload.busy} onClick={() => void upload.run()}>
                    {upload.busy ? 'Загружаем…' : 'Загрузить'}
                  </button>
                  {upload.error && <div className="notice err">{upload.error}</div>}
                  {upload.ok && <div className="notice ok">Коды загружены, дубликаты пропущены</div>}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function NewProduct({ categories, onCreated }: { categories: Category[]; onCreated: () => void }) {
  const [form, setForm] = useState({
    categoryId: '',
    sku: '',
    title: '',
    description: '',
    priceBonuses: 500,
    deliveryKind: 'CODE_POOL',
    promoNominalRub: 500,
    perUserLimit: '',
  });

  const create = useAction(async () => {
    await api.createProduct({
      categoryId: form.categoryId,
      sku: form.sku,
      title: form.title,
      description: form.description,
      priceBonuses: Number(form.priceBonuses),
      deliveryKind: form.deliveryKind,
      promoNominalKopecks: form.deliveryKind === 'PROMO_ON_DEMAND' ? Math.round(form.promoNominalRub * 100) : null,
      perUserLimit: form.perUserLimit ? Number(form.perUserLimit) : null,
      isActive: false,
    });
    onCreated();
  });

  const ready = form.categoryId && form.sku.trim() && form.title.trim();

  return (
    <>
      <h2>Новый товар</h2>
      <div className="card">
        {create.error && <div className="notice err">{create.error}</div>}
        {create.ok && <div className="notice ok">Товар создан. Он скрыт — включите его, когда всё готово.</div>}
        <div className="grid cols-3">
          <div className="field">
            <label>Категория</label>
            <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
              <option value="">— выберите —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Артикул</label>
            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          </div>
          <div className="field">
            <label>Название</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="field">
            <label>Цена в бонусах</label>
            <input
              type="number"
              min={1}
              value={form.priceBonuses}
              onChange={(e) => setForm({ ...form, priceBonuses: Number(e.target.value) })}
            />
          </div>
          <div className="field">
            <label>Способ выдачи</label>
            <select value={form.deliveryKind} onChange={(e) => setForm({ ...form, deliveryKind: e.target.value })}>
              <option value="CODE_POOL">Код из загруженного пула</option>
              <option value="PROMO_ON_DEMAND">Промокод на скидку, выпуск по запросу</option>
              <option value="NONE">Без выдачи</option>
            </select>
          </div>
          {form.deliveryKind === 'PROMO_ON_DEMAND' && (
            <div className="field">
              <label>Номинал промокода, ₽</label>
              <input
                type="number"
                min={1}
                value={form.promoNominalRub}
                onChange={(e) => setForm({ ...form, promoNominalRub: Number(e.target.value) })}
              />
            </div>
          )}
          <div className="field">
            <label>Лимит на ученика (пусто — без ограничения)</label>
            <input value={form.perUserLimit} onChange={(e) => setForm({ ...form, perUserLimit: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Описание</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <button disabled={!ready || create.busy} onClick={() => void create.run()}>
          {create.busy ? 'Создаём…' : 'Создать'}
        </button>
      </div>
    </>
  );
}
